/**
 * TypeScript types for Asset Management
 */

// Enums
export enum AssetType {
  STATIONARY = "STATIONARY",
  MOBILE = "MOBILE",
  PRINT = "PRINT",
  DIGITAL = "DIGITAL",
  SEATING = "SEATING",
}

export enum AssetStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  ARCHIVED = "ARCHIVED",
}

export enum VenueType {
  STADIUM = "STADIUM",
  ARENA = "ARENA",
  THEATER = "THEATER",
  CONFERENCE_CENTER = "CONFERENCE_CENTER",
  RETAIL_STORE = "RETAIL_STORE",
  RESTAURANT = "RESTAURANT",
  HOTEL = "HOTEL",
  OFFICE_BUILDING = "OFFICE_BUILDING",
  TRANSPORTATION_HUB = "TRANSPORTATION_HUB",
  OUTDOOR_SPACE = "OUTDOOR_SPACE",
  OTHER = "OTHER",
}

export enum ExportFormat {
  CSV = "CSV",
  EXCEL = "EXCEL",
}

// Base interfaces
export interface AIConversationContent {
  topic?: string;
  content?: string;
  context?: string;
  keywords?: string[];
  conversationStarters?: string[];
}

// Metadata interfaces
export interface StationaryMetadata extends AIConversationContent {
  latitude?: number;
  longitude?: number;
  elevation?: number;
  floor?: string;
  level?: string;
  locationDescription: string;
  accessInstructions?: string;
}

export interface MobileMetadata extends AIConversationContent {
  vehicleId: string;
  vehicleType: string;
  operator: string;
  route?: string;
  schedule?: string;
  trackingMethod: string;
}

export interface PrintMetadata extends AIConversationContent {
  publication: string;
  publicationType: string;
  issueDate?: string;
  pageNumber?: number;
  adSize: string;
  circulation?: number;
}

export interface DigitalMetadata extends AIConversationContent {
  platform: string;
  platformType: string;
  conversationId?: string;
  placement: string;
  targetAudience?: string;
  impressions?: number;
}

export interface SeatingMetadata extends AIConversationContent {
  section: string;
  level: string;
  seatStart: string;
  seatEnd: string;
  seatCount: number;
  accessibility?: boolean;
  premium?: boolean;
}

export type AssetMetadata =
  | StationaryMetadata
  | MobileMetadata
  | PrintMetadata
  | DigitalMetadata
  | SeatingMetadata;

// Venue types
export interface VenueContact {
  phone?: string;
  email?: string;
  website?: string;
  contactPerson?: string;
}

export interface Venue {
  id: string;
  tenantId: string;
  name: string;
  type: VenueType;
  address: string;
  city: string;
  state?: string;
  country: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  capacity?: number;
  contactInfo?: VenueContact;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  version: number;
}

// Asset file types
export interface AssetFile {
  id: string;
  filename: string;
  contentType: string;
  size: number;
  s3Key: string;
  uploadedAt: string;
  uploadedBy: string;
}

// Asset types
export interface Asset {
  id: string;
  tenantId: string;
  name: string;
  type: AssetType;
  status: AssetStatus;
  metadata: AssetMetadata;
  venue?: Venue;
  files: AssetFile[];
  conversationIds: string[]; // Array but enforce single active conversation through business logic
  parentAssetId?: string;
  bulkIndex?: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  version: number;
}

// Connection types
export interface AssetConnection {
  items: Asset[];
  nextToken?: string;
  total: number;
}

export interface VenueConnection {
  items: Venue[];
  nextToken?: string;
  total: number;
}

// Filter types
export interface AssetFilter {
  type?: AssetType;
  status?: AssetStatus;
  venueId?: string;
  conversationId?: string;
  conversationIds?: string[];
  createdFrom?: string;
  createdTo?: string;
  search?: string;
  tags?: string[];
}

export interface VenueFilter {
  type?: VenueType;
  city?: string;
  state?: string;
  country?: string;
  search?: string;
}

// Input types
export interface CreateVenueInput {
  name: string;
  type: VenueType;
  address: string;
  city: string;
  state?: string;
  country: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  capacity?: number;
  contactInfo?: VenueContact;
}

export interface UpdateVenueInput {
  id: string;
  name?: string;
  type?: VenueType;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  capacity?: number;
  contactInfo?: VenueContact;
}

export interface FileUploadInput {
  filename: string;
  contentType: string;
  size: number;
  description?: string;
}

export interface VariableMetadataInput {
  field: string;
  pattern: string;
  startValue?: string;
  increment?: string;
}

export interface BulkCreateConfigInput {
  count: number;
  namingPattern: string;
  variableMetadata: VariableMetadataInput[];
}

export interface AssetMetadataInput {
  stationary?: Omit<StationaryMetadata, keyof AIConversationContent> &
    Partial<AIConversationContent>;
  mobile?: Omit<MobileMetadata, keyof AIConversationContent> &
    Partial<AIConversationContent>;
  print?: Omit<PrintMetadata, keyof AIConversationContent> &
    Partial<AIConversationContent>;
  digital?: Omit<DigitalMetadata, keyof AIConversationContent> &
    Partial<AIConversationContent>;
  seating?: Omit<SeatingMetadata, keyof AIConversationContent> &
    Partial<AIConversationContent>;
}

export interface CreateAssetInput {
  name: string;
  type: AssetType;
  metadata: AssetMetadataInput;
  venueId?: string;
  files?: FileUploadInput[];
  bulkConfig?: BulkCreateConfigInput;
}

export interface UpdateAssetInput {
  name?: string;
  type?: AssetType;
  status?: AssetStatus;
  metadata?: AssetMetadataInput;
  venueId?: string;
  files?: FileUploadInput[];
}

export interface BulkCreateAssetInput {
  name: string;
  type: AssetType;
  metadata: AssetMetadataInput;
  venueId?: string;
  files?: FileUploadInput[];
  bulkConfig: BulkCreateConfigInput;
}

// Export result types
export interface AssetExportResult {
  downloadUrl: string;
  filename: string;
  recordCount: number;
  expiresAt: string;
}

// Upload URL type
export interface UploadUrl {
  uploadUrl: string;
  fileId: string;
  s3Key: string;
}

// Form data types for UI
export interface AssetFormData {
  name: string;
  type: AssetType;
  venueId?: string;
  venue?: Venue;
  metadata: Partial<AssetMetadata>;
  files?: File[];
  bulkConfig?: {
    enabled: boolean;
    count: number;
    namingPattern: string;
    variableMetadata: VariableMetadataInput[];
  };
}

export interface VenueFormData {
  name: string;
  type: VenueType;
  address: string;
  city: string;
  state?: string;
  country: string;
  zipCode?: string;
  latitude?: number;
  longitude?: number;
  description?: string;
  capacity?: number;
  contactInfo?: VenueContact;
}

// UI State types
export interface AssetListState {
  assets: Asset[];
  loading: boolean;
  error?: string;
  filter: AssetFilter;
  searchQuery: string;
  selectedAssets: string[];
  viewMode: "grid" | "list";
  sortBy: "name" | "type" | "createdAt" | "updatedAt";
  sortOrder: "asc" | "desc";
}

export interface VenueListState {
  venues: Venue[];
  loading: boolean;
  error?: string;
  filter: VenueFilter;
  searchQuery: string;
  selectedVenues: string[];
}

// Asset type display helpers
export const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  [AssetType.STATIONARY]: "Stationary",
  [AssetType.MOBILE]: "Mobile",
  [AssetType.PRINT]: "Print",
  [AssetType.DIGITAL]: "Digital",
  [AssetType.SEATING]: "Seating",
};

export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  [AssetStatus.ACTIVE]: "Active",
  [AssetStatus.INACTIVE]: "Inactive",
  [AssetStatus.ARCHIVED]: "Archived",
};

export const VENUE_TYPE_LABELS: Record<VenueType, string> = {
  [VenueType.STADIUM]: "Stadium",
  [VenueType.ARENA]: "Arena",
  [VenueType.THEATER]: "Theater",
  [VenueType.CONFERENCE_CENTER]: "Conference Center",
  [VenueType.RETAIL_STORE]: "Retail Store",
  [VenueType.RESTAURANT]: "Restaurant",
  [VenueType.HOTEL]: "Hotel",
  [VenueType.OFFICE_BUILDING]: "Office Building",
  [VenueType.TRANSPORTATION_HUB]: "Transportation Hub",
  [VenueType.OUTDOOR_SPACE]: "Outdoor Space",
  [VenueType.OTHER]: "Other",
};

// Asset type icons and colors
export const ASSET_TYPE_CONFIG: Record<
  AssetType,
  { icon: string; color: string; bgColor: string }
> = {
  [AssetType.STATIONARY]: {
    icon: "📍",
    color: "text-blue-600",
    bgColor: "bg-blue-100",
  },
  [AssetType.MOBILE]: {
    icon: "🚗",
    color: "text-green-600",
    bgColor: "bg-green-100",
  },
  [AssetType.PRINT]: {
    icon: "📄",
    color: "text-red-600",
    bgColor: "bg-red-100",
  },
  [AssetType.DIGITAL]: {
    icon: "💻",
    color: "text-purple-600",
    bgColor: "bg-purple-100",
  },
  [AssetType.SEATING]: {
    icon: "🪑",
    color: "text-orange-600",
    bgColor: "bg-orange-100",
  },
};
